import { Plus, Compass, Lightbulb } from 'lucide-react'
import type { NodeCategory } from '@bandos/shared'
import { CATEGORY_STYLES } from '../../../../constants/mindmapConfig'

// Helper function to get category style
export const getCategoryStyle = (category: NodeCategory) => {
  return CATEGORY_STYLES[category as keyof typeof CATEGORY_STYLES]
}

// Helper to convert hex to rgba
export const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Phase-aware tooltips for idea nodes
// Child phase = parent phase + 1 (capped at 6)
const getIdeaChildTooltip = (parentPhase: number | undefined): string => {
  const childPhase = parentPhase ? Math.min(parentPhase + 1, 6) : 1
  switch (childPhase) {
    case 1:
      return 'What directions to explore?' // Explore: strategic directions
    case 2:
      return 'How would this actually work?' // Shape: mechanisms
    case 3:
      return 'What would users experience?' // Define: features
    case 4:
      return 'What medium reaches the user?' // Surface: delivery touchpoint
    case 5:
      return 'What information goes in this surface?' // Structure: info hierarchy
    case 6:
      return 'What smart details make this shine?' // Polish: UX nuance
    default:
      return 'What directions to explore?'
  }
}

// Helper function to get child node info based on parent category
// Returns the icon component, color, label, and tooltip for the child node type
export const getChildNodeInfo = (
  parentCategory: NodeCategory,
  parentPhase?: number
): {
  icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
  color: string
  label: string
  tooltip: string
} => {
  switch (parentCategory) {
    case 'venture':
      return {
        icon: Compass,
        color: CATEGORY_STYLES.need.iconColor,
        label: 'Unmet need',
        tooltip: 'What problem is worth solving?',
      }
    case 'need':
      // Need creates Phase 1 Idea children
      return {
        icon: Lightbulb,
        color: CATEGORY_STYLES.idea.iconColor,
        label: 'Idea',
        tooltip: 'What directions to explore?',
      }
    case 'idea':
      // Idea creates next-phase Idea children
      return {
        icon: Lightbulb,
        color: CATEGORY_STYLES.idea.iconColor,
        label: 'Idea',
        tooltip: getIdeaChildTooltip(parentPhase),
      }
    case 'insight':
      return {
        icon: Compass,
        color: CATEGORY_STYLES.need.iconColor,
        label: 'Unmet need',
        tooltip: 'Start a new path with a validated unmet need',
      }
    case 'information':
    case 'summarized':
    case 'storyboard':
    default:
      // Default fallback
      return {
        icon: Plus,
        color: CATEGORY_STYLES.information.iconColor,
        label: 'Node',
        tooltip: 'Add a connected node',
      }
  }
}
